import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowRight, Scale } from "lucide-react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"
import type { IntakeLoad } from "@/features/intake/api/types"
import { SectionCard } from "@/features/intake/components/section-card"
import { useSaveScale } from "@/features/intake/hooks/use-intake-mutations"
import {
  computeNetWeight,
  SAC_TARE_KG,
} from "@/features/intake/lib/load-formulas"
import { formatNumber, kgToMt } from "@/lib/format"

const positiveNumber = z
  .union([z.string(), z.number()])
  .transform((v) => Number(v))
  .refine((n) => !Number.isNaN(n) && n > 0)

const scaleSchema = z
  .object({
    grossWeight: positiveNumber,
    tareWeight: positiveNumber,
    bagsUnloaded: positiveNumber,
  })
  .refine((v) => v.tareWeight < v.grossWeight, {
    path: ["tareWeight"],
    message: "tareOver",
  })

type ScaleFormInput = z.input<typeof scaleSchema>
type ScaleFormOutput = z.output<typeof scaleSchema>

interface ScaleCardProps {
  load: IntakeLoad
  // When true, inputs are disabled and the submit row is hidden — used
  // when navigating back to this step on a load that's already past
  // SCALE (or fully COMPLETED).
  readOnly?: boolean
}

export function ScaleCard({ load, readOnly = false }: ScaleCardProps) {
  const { t } = useTranslation("intake")
  const save = useSaveScale(load.id)

  const form = useForm<ScaleFormInput, unknown, ScaleFormOutput>({
    resolver: zodResolver(scaleSchema),
    defaultValues: {
      grossWeight: (load.grossWeight ?? "") as unknown as number,
      tareWeight: (load.tareWeight ?? "") as unknown as number,
      bagsUnloaded: (load.bagsUnloaded ?? "") as unknown as number,
    },
  })

  const values = form.watch()
  const gross = Number(values.grossWeight) || 0
  const tare = Number(values.tareWeight) || 0
  const bags = Number(values.bagsUnloaded) || 0
  const breakdown = computeNetWeight({
    grossWeight: gross,
    tareWeight: tare,
    bagsUnloaded: bags,
  })

  const onSubmit = form.handleSubmit((parsed) => {
    save.mutate({
      grossWeight: parsed.grossWeight,
      tareWeight: parsed.tareWeight,
      bagsUnloaded: parsed.bagsUnloaded,
    })
  })

  return (
    <SectionCard
      kicker={t("scale.kicker")}
      title={t("scale.title")}
      subtitle={t("scale.subtitle")}
      roleBadge={t("detail.role.analyst")}
    >
      <Form {...form}>
        <form onSubmit={onSubmit}>
          <fieldset disabled={readOnly} className="contents">
            <div className="grid gap-6 py-8 lg:grid-cols-[minmax(280px,360px)_minmax(320px,420px)] lg:justify-center">
              <div className="space-y-3">
                <FormField
                  control={form.control}
                  name="grossWeight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
                        {t("scale.gross")}
                      </FormLabel>
                      <FormControl>
                        <UnitInput
                          unit={t("scale.kg")}
                          type="number"
                          inputMode="decimal"
                          autoFocus={!readOnly}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="tareWeight"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel className="text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
                        {t("scale.tare")}
                      </FormLabel>
                      <FormControl>
                        <UnitInput
                          unit={t("scale.kg")}
                          type="number"
                          inputMode="decimal"
                          {...field}
                        />
                      </FormControl>
                      {fieldState.error?.message === "tareOver" ? (
                        <p className="text-xs text-destructive">
                          {t("scale.tareOver")}
                        </p>
                      ) : (
                        <FormMessage />
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bagsUnloaded"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
                        {t("scale.bags")}
                      </FormLabel>
                      <FormControl>
                        <UnitInput
                          unit={t("scale.bagsUnit")}
                          type="number"
                          inputMode="numeric"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <CalculationPane
                gross={gross}
                tare={tare}
                bags={bags}
                truckMinusTare={breakdown.truckMinusTare}
                bagsTareKg={breakdown.bagsTareKg}
                netWeight={breakdown.netWeight}
                isValid={breakdown.isValid}
              />
            </div>

            {!readOnly ? (
              <>
                <Separator className="my-5" />

                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    {breakdown.isValid
                      ? t("scale.readyHint")
                      : t("scale.fillHint")}
                  </p>
                  <Button
                    type="submit"
                    disabled={save.isPending || !breakdown.isValid}
                  >
                    {t("scale.cta")}
                    <ArrowRight className="size-4" />
                  </Button>
                </div>
              </>
            ) : null}
          </fieldset>
        </form>
      </Form>
    </SectionCard>
  )
}

interface UnitInputProps extends React.ComponentPropsWithoutRef<typeof Input> {
  unit: string
}

function UnitInput({ unit, className, ...rest }: UnitInputProps) {
  return (
    <div className="relative">
      <Input
        {...rest}
        className={cn(
          "h-10 pr-12 text-base font-medium tabular-nums",
          className
        )}
      />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-medium text-muted-foreground">
        {unit}
      </span>
    </div>
  )
}

interface CalculationPaneProps {
  gross: number
  tare: number
  bags: number
  truckMinusTare: number
  bagsTareKg: number
  netWeight: number
  isValid: boolean
}

function CalculationPane({
  gross,
  tare,
  bags,
  truckMinusTare,
  bagsTareKg,
  netWeight,
  isValid,
}: CalculationPaneProps) {
  const { t } = useTranslation("intake")

  const hasTruckPair = gross > 0 && tare > 0 && tare < gross
  const hasBags = bags > 0

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border bg-muted/30">
      <div className="border-b px-4 py-2.5">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
          <Scale className="size-3.5" />
          {t("scale.calcHeader")}
        </div>
      </div>

      <div className="flex-1 space-y-2 px-5 py-4">
        <BreakdownRow
          label={t("scale.calc.gross")}
          value={gross > 0 ? formatNumber(gross) : null}
          unit="kg"
        />
        <BreakdownRow
          label={t("scale.calc.tare")}
          value={tare > 0 ? formatNumber(tare) : null}
          unit="kg"
          tone="deduct"
        />
        <SubtotalRow
          label={t("scale.calc.subtotal")}
          sublabel={t("scale.calc.subtotalHint")}
          value={hasTruckPair ? formatNumber(truckMinusTare) : null}
          unit="kg"
        />
        <BreakdownRow
          label={t("scale.calc.sacTare")}
          sublabel={
            hasBags
              ? t("scale.calc.sacTareHint", {
                  bags,
                  rate: formatNumber(SAC_TARE_KG, 2),
                })
              : undefined
          }
          value={hasBags ? formatNumber(bagsTareKg, 2) : null}
          unit="kg"
          tone="deduct"
        />
      </div>

      <div className="border-t bg-background/60 px-5 py-4">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
              {t("scale.calc.net")}
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {t("scale.calc.netHint")}
            </div>
          </div>
        </div>
        {isValid ? (
          <>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-3xl leading-none font-semibold tabular-nums">
                {formatNumber(netWeight)}
              </span>
              <span className="text-base text-muted-foreground">kg</span>
            </div>
            <div className="mt-1.5 text-[11px] text-muted-foreground tabular-nums">
              ≈ {formatNumber(kgToMt(netWeight), 2)} MT
            </div>
          </>
        ) : (
          <div className="mt-2 text-3xl leading-none text-muted-foreground/40 tabular-nums">
            —
          </div>
        )}
      </div>
    </div>
  )
}

interface SubtotalRowProps {
  label: string
  sublabel: string
  value: string | null
  unit: string
}

function SubtotalRow({ label, sublabel, value, unit }: SubtotalRowProps) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-dashed border-border/60 pt-2 text-sm">
      <div className="min-w-0 truncate">
        <div className="truncate font-medium text-foreground/90">{label}</div>
        <div className="truncate text-[11px] text-muted-foreground">
          {sublabel}
        </div>
      </div>
      <div className="flex shrink-0 items-baseline gap-1 tabular-nums">
        {value === null ? (
          <span className="text-muted-foreground/40">—</span>
        ) : (
          <>
            <span className="font-semibold text-foreground">{value}</span>
            <span className="text-[11px] text-muted-foreground">{unit}</span>
          </>
        )}
      </div>
    </div>
  )
}

interface BreakdownRowProps {
  label: string
  sublabel?: string
  value: string | null
  unit: string
  tone?: "deduct"
}

function BreakdownRow({ label, sublabel, value, unit, tone }: BreakdownRowProps) {
  const isDeduct = tone === "deduct"
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <div className="min-w-0 truncate">
        <div className="truncate text-foreground/85">{label}</div>
        {sublabel ? (
          <div className="truncate text-[11px] text-muted-foreground">
            {sublabel}
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-baseline gap-1 tabular-nums">
        {value === null ? (
          <span className="text-muted-foreground/40">—</span>
        ) : (
          <>
            <span
              className={cn(
                "font-medium",
                isDeduct ? "text-foreground/75" : "text-foreground"
              )}
            >
              {isDeduct ? `−${value}` : value}
            </span>
            <span className="text-[11px] text-muted-foreground">{unit}</span>
          </>
        )}
      </div>
    </div>
  )
}
